import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import API from "@/lib/api";
import LandingPage from "@/pages/LandingPage";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import BusinessDashboard from "@/pages/BusinessDashboard";
import OnboardingPage from "@/pages/OnboardingPage";
import PublicPolicyPage from "@/pages/PublicPolicyPage";
import ConsentVault from "@/pages/ConsentVault";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import PricingPage from "@/pages/PricingPage";

const AuthContext = createContext(null);

export function useAuth() {
    return useContext(AuthContext);
}

function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        try {
            const res = await API.get("/auth/me", { withCredentials: true });
            setUser(res.data);
        } catch {
            setUser(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => { checkAuth(); }, [checkAuth]);

    const logout = async () => {
        try { await API.post("/auth/logout", {}, { withCredentials: true }); } catch {}
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, setUser, logout, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

function AuthCallback() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [processed, setProcessed] = useState(false);

    useEffect(() => {
        if (processed) return;
        setProcessed(true);

        const hash = location.hash;
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (!sessionId) { navigate('/', { replace: true }); return; }

        // Exchange session_id for persistent session
        API.post("/auth/session", { session_id: sessionId }, { withCredentials: true })
            .then(res => {
                setUser(res.data);
                const accountType = res.data.account_type;
                if (accountType === "executive") navigate("/dashboard/executive", { replace: true });
                else if (accountType === "business") navigate("/dashboard/business", { replace: true });
                else navigate("/onboarding", { replace: true });
            })
            .catch(() => navigate('/?error=auth_failed', { replace: true }));
    }, [location, navigate, setUser, processed]);

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
    );
}

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
    );
    if (!user) return <Navigate to="/" replace />;
    return children;
}

function AppRouter() {
    const location = useLocation();
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (location.hash?.includes('session_id=')) return <AuthCallback />;

    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/vault" element={<ConsentVault />} />
            <Route path="/u/:handle" element={<PublicPolicyPage />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/dashboard/executive" element={<ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/business" element={<ProtectedRoute><BusinessDashboard /></ProtectedRoute>} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <div className="noise-overlay" />
                <Toaster
                    theme="dark" richColors position="bottom-right"
                    toastOptions={{
                        style: {
                            background: "hsl(0 0% 7%)", border: "1px solid rgba(255,255,255,0.08)",
                            color: "#F9FAFB", fontFamily: "Manrope, sans-serif",
                        },
                    }}
                />
                <AppRouter />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
